import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Sprout, Grid, Scissors } from 'lucide-react';
import { contactInfo, fallbackServices } from '../data';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [leaveTimeoutId, setLeaveTimeoutId] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (leaveTimeoutId) clearTimeout(leaveTimeoutId);
    };
  }, [leaveTimeoutId]);

  // Scroll lock when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setServices(data);
          } else {
            setServices(fallbackServices);
          }
        } else {
          setServices(fallbackServices);
        }
      } catch (error) {
        console.error('Fejl ved hentning af ydelser:', error);
        setServices(fallbackServices);
      }
    };

    fetchServices();
  }, []);

  const handleNavClick = (id) => {
    setIsOpen(false);
    setIsDropdownOpen(false);
    setIsMobileDropdownOpen(false);
    
    if (location.pathname !== '/') {
      // Navigate to home page first
      navigate('/');
      // Wait for route change and DOM rendering before scrolling
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const offset = 64; // reduced height of header
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 150);
    } else {
      // Already on home page, just scroll
      const element = document.getElementById(id);
      if (element) {
        const offset = 64; // reduced height of header
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const navItems = [
    { label: 'Forside', id: 'forside', isRoute: false },
    { label: 'Jeg hjælper med', id: 'ydelser', isRoute: false },
    { label: 'Galleri', id: 'galleri', isRoute: true, path: '/galleri' },
    { label: 'Kunderne siger', id: 'udtalelser', isRoute: false },
    { label: 'Om mig', id: 'om-mig', isRoute: false },
    { label: 'Kontakt', id: 'kontakt', isRoute: false }
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 bg-emerald-950 border-b border-emerald-900/50 shadow-lg ${
        isScrolled ? 'py-1.5 bg-emerald-950/95 backdrop-blur-md shadow-xl' : 'py-2.5'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div 
              onClick={() => handleNavClick('forside')} 
              className="flex items-center gap-3 cursor-pointer group"
            >
              <div className="scale-105">
                <img 
                  src="https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/60bf1a3b-28de-4794-b622-da4f507d9436.png" 
                  alt="BP Anlægsgartner" 
                  className={`w-auto object-contain transition-all duration-300 ${
                    isScrolled 
                      ? 'h-10 sm:h-12 md:h-14 lg:h-16' 
                      : 'h-14 sm:h-16 md:h-20 lg:h-24'
                  }`}
                />
              </div>
            </div>

            {/* Desktop Navigation (lg and up) */}
            <nav className="hidden lg:flex items-center gap-6 lg:gap-8">
              {navItems.map((item) => {
                if (item.id === 'ydelser') {
                  const handleMouseEnter = () => {
                    if (leaveTimeoutId) {
                      clearTimeout(leaveTimeoutId);
                      setLeaveTimeoutId(null);
                    }
                    setIsDropdownOpen(true);
                  };

                  const handleMouseLeave = () => {
                    const timeoutId = setTimeout(() => {
                      setIsDropdownOpen(false);
                    }, 250); // 250ms delay gives a smooth user experience
                    setLeaveTimeoutId(timeoutId);
                  };

                  return (
                    <div 
                      key={item.id}
                      className="relative py-2"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <button
                        onClick={() => {
                          if (isDropdownOpen) {
                            setIsDropdownOpen(false);
                          } else {
                            handleMouseEnter();
                          }
                        }}
                        className="text-xs font-bold text-white/80 hover:text-white transition-colors cursor-pointer relative py-1 uppercase tracking-wider flex items-center gap-1"
                      >
                        <span>{item.label}</span>
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown Menu */}
                      <div className={`absolute left-1/2 -translate-x-1/2 mt-2 w-64 bg-emerald-950 border border-emerald-900/80 rounded-xl shadow-2xl py-1.5 transition-all duration-200 origin-top ${
                        isDropdownOpen 
                          ? 'opacity-100 scale-100 pointer-events-auto translate-y-0' 
                          : 'opacity-0 scale-95 pointer-events-none -translate-y-2'
                      }`}>
                        {services.map((service, idx) => {
                          const titleText = typeof service.title === 'object' ? service.title.da : service.title;
                          const slug = typeof service.slug === 'object' ? service.slug.da : service.slug;
                          
                          return (
                            <Link
                              key={service.id || idx}
                              to={`/ydelser/${slug}`}
                              onClick={() => setIsDropdownOpen(false)}
                              className="block px-5 py-2.5 hover:bg-emerald-900/40 text-stone-200 hover:text-white transition-colors"
                            >
                              <span className="text-xs font-semibold uppercase tracking-wider">{titleText}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                if (item.isRoute) {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`text-xs font-bold transition-colors cursor-pointer relative py-1 uppercase tracking-wider after:absolute after:bottom-0 after:left-0 after:h-[2px] after:transition-all after:duration-300 ${
                        isActive 
                          ? 'text-white after:w-full after:bg-emerald-400' 
                          : 'text-white/80 hover:text-white after:w-0 hover:after:w-full after:bg-white'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                }

                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    className="text-xs font-bold text-white/80 hover:text-white transition-colors cursor-pointer relative py-1 uppercase tracking-wider after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 hover:after:w-full after:bg-white after:transition-all after:duration-300"
                  >
                    {item.label}
                  </button>
                );
              })}
            </nav>

            {/* Call to Action Button (lg and up) */}
            <div className="hidden lg:flex items-center gap-4">
              <a
                href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`}
                className="flex items-center gap-2 text-sm font-bold text-white bg-emerald-700 hover:bg-emerald-600 px-5 py-2.5 rounded-full transition-all shadow-md hover:shadow-emerald-950/30 hover:-translate-y-0.5"
              >
                <Phone className="h-4 w-4 text-emerald-100" />
                {contactInfo.phone}
              </a>
            </div>

            {/* Hamburger menu button (shown on md and mobile, hidden on lg) */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsOpen(true)}
                className="p-2 rounded-lg text-emerald-100 hover:text-white hover:bg-emerald-900/50 transition-colors cursor-pointer"
                aria-label="Åbn menu"
              >
                <Menu className="h-6 w-6 sm:h-7 sm:w-7" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar Backdrop Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300 lg:hidden ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar Drawer Menu */}
      <div className={`fixed top-0 right-0 bottom-0 w-full max-w-xs sm:max-w-sm bg-emerald-950 z-50 shadow-2xl border-l border-emerald-900/50 flex flex-col transition-transform duration-300 ease-in-out lg:hidden ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Sidebar Header with Logo and Close button */}
        <div className="p-4 sm:p-5 border-b border-emerald-900/40 flex items-center justify-between bg-emerald-950/90 backdrop-blur-md">
          <div 
            onClick={() => {
              handleNavClick('forside');
              setIsOpen(false);
            }} 
            className="cursor-pointer"
          >
            <img 
              src="https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/594eaf4a-ed99-49a0-bf64-94af839ff8a9.png" 
              alt="BP Anlægsgartner Logo" 
              className="h-24 sm:h-28 w-auto object-contain"
            />
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-lg text-emerald-100 hover:text-white hover:bg-emerald-900/50 transition-colors cursor-pointer"
            aria-label="Luk menu"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Sidebar Navigation Links */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-2">
          {navItems.map((item) => {
            if (item.id === 'ydelser') {
              return (
                <div key={item.id} className="space-y-1">
                  <button
                    onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                    className="flex items-center justify-between w-full text-left px-4 py-3 rounded-xl text-sm font-bold text-white/80 hover:text-white hover:bg-emerald-900/40 transition-colors uppercase tracking-wider cursor-pointer"
                  >
                    <span>{item.label}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isMobileDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <div className={`pl-4 space-y-1 overflow-hidden transition-all duration-300 ${
                    isMobileDropdownOpen ? 'max-h-[400px] opacity-100 mt-1' : 'max-h-0 opacity-0'
                  }`}>
                    {services.map((service, idx) => {
                      const titleText = typeof service.title === 'object' ? service.title.da : service.title;
                      const slug = typeof service.slug === 'object' ? service.slug.da : service.slug;
                      
                      return (
                        <Link
                          key={service.id || idx}
                          to={`/ydelser/${slug}`}
                          onClick={() => {
                            setIsOpen(false);
                            setIsMobileDropdownOpen(false);
                          }}
                          className="block px-4 py-2.5 rounded-lg text-xs font-bold text-stone-300 hover:text-white hover:bg-emerald-900/30 transition-colors uppercase tracking-wider"
                        >
                          <span>{titleText}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            }

            if (item.isRoute) {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`block w-full text-left px-4 py-3 rounded-xl text-sm font-bold transition-colors uppercase tracking-wider ${
                    isActive 
                      ? 'text-white bg-emerald-900/40' 
                      : 'text-white/80 hover:text-white hover:bg-emerald-900/40'
                  }`}
                >
                  {item.label}
                </Link>
              );
            }

            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className="block w-full text-left px-4 py-3 rounded-xl text-sm font-bold text-white/80 hover:text-white hover:bg-emerald-900/40 transition-colors uppercase tracking-wider cursor-pointer"
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Sidebar Footer with CTA */}
        <div className="p-4 sm:p-6 border-t border-emerald-900/40 bg-emerald-950/80">
          <a
            href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`}
            className="flex items-center justify-center gap-2 w-full text-center text-xs sm:text-sm font-bold text-white bg-emerald-700 hover:bg-emerald-600 py-2.5 sm:py-3 rounded-full transition-colors shadow-lg"
          >
            <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-100" />
            {contactInfo.phone}
          </a>
        </div>
      </div>
    </>
  );
}
